import React, { useState } from 'react';
import { X, CheckCircle, CreditCard, Truck } from 'lucide-react';
import '../../styles/marketplace.css';

import { marketplaceAPI } from '../../services/marketplaceAPI';

const CheckoutModal = ({ isOpen, onClose, cartItems, cartTotal, currency, rate = 1, onPlaceOrder }) => {
    if (!isOpen) return null;

    const [step, setStep] = useState(1); // 1: Shipping, 2: Payment, 3: Success
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        city: '',
        phone: '',
        cardNumber: '',
        expiry: '',
        cvv: ''
    });

    // Handle keyboard events (Escape to close)
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    const handleChange = (e) => {
        let value = e.target.value;
        if (e.target.name === 'phone') {
            value = value.replace(/[^0-9\+\-\s]/g, '');
        }
        setFormData({ ...formData, [e.target.name]: value });
    };

    const handleNext = (e) => {
        e.preventDefault();
        setStep(step + 1);
    };

    const handleSubmitOrder = async () => {
        setLoading(true);
        setError(null);

        try {
            const orderData = {
                items: cartItems.map(item => ({
                    id: item.id,
                    title: item.title,
                    quantity: item.quantity,
                    price: item.price
                })),
                total: cartTotal,
                currency: currency,
                customer: formData.name,
                shippingAddress: `${formData.address}, ${formData.city}`
            };

            const response = await marketplaceAPI.createOrder(orderData);

            if (response.status === 'success') {
                setStep(3);
                // We delay the actual clearing of cart in parent to let user see success message
            } else {
                setError(response.message || 'Failed to place order.');
            }
        } catch (err) {
            console.error("Order failed:", err);
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (step === 3) {
        return (
            <div className="mp-modal-overlay">
                <div className="mp-modal-content" style={{ textAlign: 'center', padding: '60px 40px', position: 'relative' }}>
                    <button
                        onClick={() => onPlaceOrder()}
                        className="mp-modal-close"
                        style={{ position: 'absolute', top: '16px', right: '16px' }}
                    >
                        <X size={24} />
                    </button>
                    <div style={{
                        width: '80px', height: '80px', background: '#10B981', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px'
                    }}>
                        <CheckCircle size={48} color="white" />
                    </div>
                    <h2 style={{ fontSize: '28px', marginBottom: '16px' }}>Order Placed Successfully!</h2>
                    <p style={{ color: '#666', fontSize: '16px', marginBottom: '24px' }}>Thank you for your purchase. Your pets will love it!</p>

                    <button onClick={() => { console.log("Manual Close Clicked"); onPlaceOrder(); }} className="mp-btn-primary full-width">
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mp-modal-overlay">
            <div className="mp-modal-content" style={{ maxWidth: '600px' }}>
                <div className="mp-modal-header">
                    <h2>Checkout</h2>
                    <button onClick={onClose} className="mp-modal-close"><X size={24} /></button>
                </div>

                <div className="mp-checkout-steps">
                    <div className={`mp-step ${step >= 1 ? 'active' : ''}`}>
                        <div className="mp-step-icon"><Truck size={18} /></div>
                        <span>Shipping</span>
                    </div>
                    <div className="mp-step-line"></div>
                    <div className={`mp-step ${step >= 2 ? 'active' : ''}`}>
                        <div className="mp-step-icon"><CreditCard size={18} /></div>
                        <span>Payment</span>
                    </div>
                </div>

                <form onSubmit={step === 1 ? handleNext : (e) => { e.preventDefault(); handleSubmitOrder(); }}>
                    <div className="mp-modal-body">
                        {step === 1 ? (
                            <div className="mp-form-grid">
                                <div className="mp-input-group">
                                    <label>Full Name</label>
                                    <input required type="text" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" />
                                </div>
                                <div className="mp-input-group">
                                    <label>Phone Number</label>
                                    <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+966 5..." />
                                </div>
                                <div className="mp-input-group full">
                                    <label>Address</label>
                                    <input required type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Street, Building, Apt..." />
                                </div>
                                <div className="mp-input-group full">
                                    <label>City</label>
                                    <input required type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Riyadh" />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="mp-order-summary">
                                    <h3>Order Summary</h3>
                                    {cartItems.map(item => (
                                        <div key={item.id} className="mp-summary-item">
                                            <span>{item.quantity}x {item.title}</span>
                                            <span>{(item.price * item.quantity * rate).toFixed(2)} {currency}</span>
                                        </div>
                                    ))}
                                    <div className="mp-summary-total">
                                        <span>Total</span>
                                        <span>{cartTotal.toFixed(2)} {currency}</span>
                                    </div>
                                </div>

                                <div className="mp-form-grid" style={{ marginTop: '24px' }}>
                                    <div className="mp-input-group full">
                                        <label>Card Number</label>
                                        <input required type="text" placeholder="0000 0000 0000 0000" />
                                    </div>
                                    <div className="mp-input-group">
                                        <label>Expiry</label>
                                        <input required type="text" placeholder="MM/YY" />
                                    </div>
                                    <div className="mp-input-group">
                                        <label>CVV</label>
                                        <input required type="text" placeholder="123" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mp-modal-footer">
                        {step === 2 && (
                            <button type="button" onClick={() => setStep(1)} className="mp-btn-secondary">
                                Back
                            </button>
                        )}
                        <button type="submit" disabled={loading} className="mp-btn-primary full-width">
                            {loading ? 'Processing...' : step === 1 ? 'Continue to Payment' : `Pay ${cartTotal.toFixed(2)} ${currency}`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CheckoutModal;
